import Router from 'next/router';

import { ApolloClient, InMemoryCache, HttpLink, from, } from '@apollo/client';
import { onError } from '@apollo/client/link/error';

import { setContext } from '@apollo/client/link/context';
import {
  customFetch,
  defaultTimeZone,
} from '@shared/library/apolloClientHelper';
import { getSession, signOut } from 'next-auth/react';
import { authClient } from '../lib/auth-client';
import { triggerEvent } from '@shared/core-library/src/globalStore/events';
import { clearData } from '@shared/core-library/src/globalStore';

const httpLink = new HttpLink({ fetch: customFetch });

const handleUserLoggingOut = async () => {
  if (typeof window === 'undefined') return;
  clearData();
  cachedBetterAuthToken = null;
  // eslint-disable-next-line no-use-before-define
  apolloClient?.clearStore();
  await authClient.signOut();
  await signOut({ redirect: false });
  Router.push('/login', '/login', { shallow: true });
  triggerEvent('LOG_OUT');
};

const isTokenExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.exp * 1000 < Date.now() + 60_000; // refresh 1min before expiry
  } catch {
    return true;
  }
};

let cachedBetterAuthToken = null;
let tokenFetchPromise = null;

const authLink = setContext(async (_, { headers }) => {
  const session = await getSession();
  let token = session?.accessToken;

  if (token && session?.isBetterAuth) {
    if (!cachedBetterAuthToken || isTokenExpired(cachedBetterAuthToken)) {
      if (!tokenFetchPromise) {
        tokenFetchPromise = authClient.$fetch('/token').then(({ data, error }) => {
          if (data?.token) cachedBetterAuthToken = data.token;
          else if (error?.status === 401) handleUserLoggingOut();
        }).catch(() => {}).finally(() => { tokenFetchPromise = null; });
      }
      await tokenFetchPromise;
    }
    if (cachedBetterAuthToken) token = cachedBetterAuthToken;
  }

  return {
    headers: {
      authorization: token ? `Bearer ${token}` : null,
      ...defaultTimeZone,
      ...headers,
    },
  };
});

const errorLink = onError(({ networkError, graphQLErrors }) => {
  const httpErrorStatus = networkError?.response?.status;
  const hasGraphQL401 = graphQLErrors?.some((e) => e?.extensions?.code === '401');
  const logoutUser = httpErrorStatus === 401 || hasGraphQL401;

  if (logoutUser) {
    handleUserLoggingOut();
  }
});

const apolloClient = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache({
    addTypename: false,
  }),
});

export { apolloClient, handleUserLoggingOut, customFetch };
